// thuriyamApi.ts — API service for the SQL Script Executor admin section.
// Calls /api/sql-executor/ endpoints (wired in backend_project/urls.py).

import axios from '../../lib/axios';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UserMinimal {
    id: number;
    username: string;
    email: string;
}

export interface SQLScript {
    id: number;
    name: string;
    description: string;
    sql_query: string;
    target_database: 's1_apply_db' | 's2_applyv2_db' | 'both';
    target_database_display: string;
    is_active: boolean;
    timeout_seconds: number;
    max_rows: number;
    created_by: UserMinimal | null;
    created_at: string;
    updated_at: string;
    execution_count: number;
}

export interface SQLScriptWrite {
    name: string;
    description: string;
    sql_query: string;
    target_database: 's1_apply_db' | 's2_applyv2_db' | 'both';
    is_active: boolean;
    timeout_seconds?: number;
    max_rows?: number;
}

export interface ScriptExecution {
    id: number;
    script: number;
    script_name: string;
    executed_by: UserMinimal | null;
    started_at: string;
    completed_at: string | null;
    status: 'pending' | 'running' | 'success' | 'error' | 'timeout';
    status_display: string;
    row_count: number | null;
    execution_time_seconds: number | null;
    error_message: string | null;
    result_data: { columns: string[]; rows: Record<string, unknown>[] } | null;
    database_used: string;
    ip_address: string | null;
}

export interface ScriptPermission {
    id: number;
    script: number;
    script_name: string;
    user: UserMinimal;
    granted_by: UserMinimal | null;
    granted_at: string;
}

export interface ExecutionListResponse {
    count: number;
    page: number;
    page_size: number;
    results: ScriptExecution[];
}

// ── Service ───────────────────────────────────────────────────────────────────

const BASE = '/api/sql-executor';

export const thuriyamService = {
    // ── Scripts ──────────────────────────────────────────────────────────────

    listScripts: (params?: { search?: string; is_active?: boolean }) =>
        axios.get<SQLScript[]>(`${BASE}/scripts/`, { params }),

    createScript: (data: SQLScriptWrite) =>
        axios.post<SQLScript>(`${BASE}/scripts/`, data),

    updateScript: (id: number, data: Partial<SQLScriptWrite>) =>
        axios.patch<SQLScript>(`${BASE}/scripts/${id}/`, data),

    deleteScript: (id: number) =>
        axios.delete(`${BASE}/scripts/${id}/`),

    executeScript: (id: number) =>
        axios.post<ScriptExecution>(`${BASE}/scripts/${id}/execute/`),

    // ── Executions ───────────────────────────────────────────────────────────

    listExecutions: (params?: { script_id?: number; status?: string; page?: number; page_size?: number }) =>
        axios.get<ExecutionListResponse>(`${BASE}/executions/`, { params }),

    exportExecutionCsv: (id: number) =>
        `${BASE}/executions/${id}/export/csv/`,

    exportExecutionPdf: (id: number) =>
        `${BASE}/executions/${id}/export/pdf/`,

    // ── Permissions ──────────────────────────────────────────────────────────

    listPermissions: (params?: { script_id?: number }) =>
        axios.get<ScriptPermission[]>(`${BASE}/permissions/`, { params }),

    grantPermission: (data: { script: number; user_id: number }) =>
        axios.post<ScriptPermission>(`${BASE}/permissions/`, data),

    revokePermission: (id: number) =>
        axios.delete(`${BASE}/permissions/${id}/`),
};
